import * as React from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { leagueSeasonApi, leagueEventApi } from '../../../adapters/APIExporter';
import { LeagueSeason, LeagueEvent, League } from '../../../sdk/model';
import {Box, Typography, InputLabel, Select, MenuItem, Button} from '@mui/material';
import LeagueEventDisplay from '../leagueEvent/LeagueEventDisplay';
import { useAppSelector } from '../../../app/hooks';
import { useLoadLeagueData } from '../../../app/dataLoaderHooks';

const PAGE_SIZE = 10;
const emptySeason = {};

function LeagueTransactions() {
  const {leagueId} = useParams();
  const [searchParams] = useSearchParams();

  const [seasons, setSeasons] = React.useState<LeagueSeason[]>([]);
  const [leagueSeason, setLeagueSeason] = React.useState<LeagueSeason | undefined>(undefined);
  const [weekNumber, setWeekNumber] = React.useState<number | undefined>(undefined);
  const [type, setType] = React.useState("ALL");
  const [leagueEvents, setLeagueEvents] = React.useState<LeagueEvent[]>([]);
  const [eventsLoaded, setEventsLoaded] = React.useState<boolean>(false);
  const [isLoadComplete, setLoadComplete] = React.useState(false);
  const [isException, setIsException] = React.useState(false);
  const [allHistoryLoaded, setAllHistoryLoaded] = React.useState(false);
  const [initiateHistoryLoad, setInitiateHistoryLoad] = React.useState(false);
  
  React.useEffect(() => {
    leagueSeasonApi.getAllLeagueSeasons(leagueId).then(response => {
      const seasons : LeagueSeason[] = response.data;
      setSeasons(seasons);
      const seasonYear = Number(searchParams.get('seasonYear'));
      const week = Number(searchParams.get('week'));
      const currentSeason = seasonYear ? seasons.filter(season => season.seasonYear === seasonYear)[0] : seasons[0];
      setLeagueSeason(currentSeason);
      setWeekNumber(week || currentSeason.currentWeek);
    });
  }, []);
  
  function loadHistory() {
    setEventsLoaded(false);
    leagueEventApi.getLeagueEvents(leagueId, leagueEvents ? leagueEvents.length : 0, leagueSeason ? leagueSeason.seasonYear : undefined, weekNumber === -1 ? undefined : weekNumber, type === "ALL" ? undefined : type).then(response => {
      const newEvents = response.data;
      setLeagueEvents(leagueEvents.concat(newEvents));
      setEventsLoaded(true);
      if(newEvents.length < PAGE_SIZE) {
        setAllHistoryLoaded(true);
      }
    });
  }
  
  React.useEffect(() => {
    if (leagueSeason !== undefined && weekNumber !== undefined) {
      setLeagueEvents([])
      setAllHistoryLoaded(false);
      setInitiateHistoryLoad(true);
    }
  }, [weekNumber, leagueSeason, type])
  
  React.useEffect(() => {
    if (initiateHistoryLoad) {
      setInitiateHistoryLoad(false)
      loadHistory()
    }
  }, [initiateHistoryLoad])
  
  const user = useAppSelector((state) => state.user);
  const playerDataLoad = useAppSelector((state) => state.playerDataLoad );
  
  const league : League|undefined = useLoadLeagueData({leagueId: leagueId, userId: user.id, loadBasicAuctionData: false, 
    loadFullAuctionData: false, loadContractData: true, loadRosterData: true,
    setLoadComplete: setLoadComplete, isLoadComplete: isLoadComplete,
    setIsException: setIsException, isException: isException});
    
    React.useEffect(() => {
      const newEvents = [...leagueEvents];
      league?.recentEvents?.forEach(event => {
        const matchingEvent = newEvents.find(newEvent => newEvent.id === event.id);
        if (matchingEvent) {
          matchingEvent.reactions = event.reactions;
        }
      })
      setLeagueEvents(newEvents);
    }, [league]);

  if (!seasons.length || !league || !playerDataLoad) {
    return "Loading..."
  }
  
  const playerMap = new Map(playerDataLoad.flatMap(team => team.players).map((player) => [player?.id, player]));
  const teamMap = new Map(league.teams!.map(team => [team.id, team]));
   
  return <Box sx={{overflow: 'auto'}}>
    <Typography variant="h4" sx={{ flexGrow: 1 }}>
      League Transactions
    </Typography>
    <Box sx={{ display: 'inline-block' }}>
      <InputLabel id="seasonYearLabel">Season</InputLabel>
      <Select
        labelId="seasonYearLabel"
        id="seasonYear"
        value={leagueSeason}
        label="Age"
        onChange={event => setLeagueSeason(event.target.value)}
      >
        {seasons.map(season => 
          <MenuItem key={season.seasonYear} value={season}>{season.seasonYear}</MenuItem>
        )}
        <MenuItem value={emptySeason}>ALL</MenuItem>
      </Select>
    </Box>
    <Box sx={{ display: 'inline-block' }}>
      <InputLabel id="weekNumberLabel">Week</InputLabel>
      <Select
        labelId="weekNumberLabel"
        id="weekNumber"
        value={weekNumber}
        label="Age"
        onChange={event => setWeekNumber(event.target.value)}
      >
         {[...Array(leagueSeason?.currentWeek ? leagueSeason!.currentWeek! + 1 :  league.currentSeason!.finalWeek! + 1).keys()].map(weekNumber => 
          <MenuItem key={weekNumber} value={weekNumber}>{weekNumber ? weekNumber : "Preseason"}</MenuItem>
        )}
        <MenuItem value={-1}>ALL</MenuItem>
      </Select>
    </Box>
    <Box sx={{ display: 'inline-block' }}>
      <InputLabel id="weekNumberLabel">Event Type</InputLabel>
      <Select
        labelId="eventTypeLabel"
        id="type"
        value={type}
        label="Event Type"
        onChange={event => setType(event.target.value)}
      >
        <MenuItem value="commissioner_add_player_event">Commissioner Add Player</MenuItem>
        <MenuItem value="contract_edit_event">Commissioner Edit Contract</MenuItem>
        <MenuItem value="delete_team">Commissioner Delete Team</MenuItem>
        <MenuItem value="contract_tag">Contract Tag</MenuItem>
        <MenuItem value="ir_transaction">IR Transaction</MenuItem>
        <MenuItem value="mid_season_extension_event">Mid-Season Extension</MenuItem>
        <MenuItem value="player_drop_event">Player Dropped</MenuItem>
        <MenuItem value="player_quick_sign_event">Player Quick-Sign</MenuItem>
        <MenuItem value="practice_squad_activation_event">Practice Squad Activation</MenuItem>
        <MenuItem value="trade">Trade</MenuItem>
        <MenuItem value="waiver_faab_event">Waiver/FAAB Event</MenuItem>
        <MenuItem value="ALL">ALL</MenuItem>
      </Select>
    </Box>
    {leagueEvents.length ? 
      leagueEvents.map((event, eventIndex) => <LeagueEventDisplay key={eventIndex} event={event} playerMap={playerMap} teamMap={teamMap} />)
      : eventsLoaded && <Typography>No Transactions</Typography>
    }
    {allHistoryLoaded ||
      <Button onClick={loadHistory} disabled={!eventsLoaded}>
        {eventsLoaded ? 'Load More' : 'Loading...'}
      </Button>
    }
  </Box>
}

export default LeagueTransactions;